import { type Card, type Config, type FaqCard, type FeatureCard, type HeroCard, type ImageCard, type LinkCard, type TextCard } from '@stackje/common/config';
import { type CommonColor } from '@stackje/common/config';

export type SiteTemplateId = 'links' | 'bio-page' | 'business-page' | 'portfolio' | 'product-page' | 'empty-canvas';

export interface SiteTemplate {
  id: SiteTemplateId;
  name: string;
  description: string;
  icon: string;
  cards: Card[];
}

const defaultColors: CommonColor = {
  background: "blue-900",
  foreground: "sky-300",
  cardBackground: "blue-500",
  cardForeground: "sky-50",
  buttonBackground: "sky-50",
  buttonForeground: "sky-950",
};

export const siteTemplates: SiteTemplate[] = [
  {
    id: 'links',
    name: 'Links',
    description: 'Perfect for sharing multiple links in one place',
    icon: '🔗',
    cards: [
      {
        type: 'link2',
        title: 'My Links',
        description: 'Find all my important links below',
        links: [
          { title: 'Instagram', url: 'https://instagram.com' },
          { title: 'Twitter', url: 'https://twitter.com' },
          { title: 'YouTube', url: 'https://youtube.com' },
          { title: 'TikTok', url: 'https://tiktok.com' },
          { title: 'Email Me', url: 'mailto:hello@example.com' }
        ],
        colors: defaultColors
      } satisfies LinkCard,
    ]
  },
  {
    id: 'bio-page',
    name: 'Bio Page',
    description: 'Showcase your personal brand and story',
    icon: '👤',
    cards: [
      {
        type: 'hero3',
        brandTitle: 'Personal Brand',
        title: 'Your Name',
        description: 'Tell your story here - what you do, what you\'re passionate about, and what makes you unique',
        button: { title: 'Contact Me', url: 'mailto:hello@example.com' },
        logo: { src: 'https://placehold.co/128x128', alt: 'Personal brand logo' },
        image: { src: 'https://placehold.co/200x200', alt: 'Profile picture' },
        backgroundImage: { src: 'https://placehold.co/800x400', alt: 'Background image' },
        socialLinks: [
          { platform: 'instagram', url: 'https://instagram.com', displayName: '@username' },
          { platform: 'x', url: 'https://x.com', displayName: '@username' },
          { platform: 'email', url: 'mailto:hello@example.com', displayName: 'Email me' }
        ],
        colors: defaultColors
      } satisfies HeroCard,
      {
        type: 'text1',
        title: 'About Me',
        subtitle: 'Share your background, experience, and what makes you unique. Talk about your journey, your passions, and what drives you.',
        colors: defaultColors
      } satisfies TextCard,
    ]
  },
  {
    id: 'business-page',
    name: 'Business Page',
    description: 'Professional page for your business',
    icon: '🏢',
    cards: [
      {
        type: 'hero1',
        brandTitle: 'Company Name',
        title: 'Your Business Tagline',
        description: 'Briefly describe what your business offers and how you help your customers succeed',
        button: { title: 'Get Started', url: 'https://example.com' },
        logo: { src: 'https://placehold.co/128x128', alt: 'Company logo' },
        image: { src: 'https://placehold.co/300x300', alt: 'Business image' },
        backgroundImage: { src: 'https://placehold.co/800x400', alt: 'Business background' },
        socialLinks: [
          { platform: 'linkedin', url: 'https://linkedin.com/company/yourcompany', displayName: 'Follow us' },
          { platform: 'email', url: 'mailto:info@example.com', displayName: 'Contact us' }
        ],
        colors: defaultColors
      } satisfies HeroCard,
      {
        type: 'feature1',
        title: 'Why Choose Us',
        subtitle: 'Key benefits of working with us',
        features: [
          {
            title: 'Professional Service',
            description: 'Experienced team dedicated to your success',
            image: 'https://placehold.co/150'
          },
          {
            title: 'Competitive Pricing',
            description: 'Great value for your investment',
            image: 'https://placehold.co/150'
          },
          {
            title: 'Fast Delivery',
            description: 'Quick turnaround times that meet your deadlines',
            image: 'https://placehold.co/150'
          }
        ],
        colors: defaultColors
      } satisfies FeatureCard,
      {
        type: 'faq1',
        title: 'Frequently Asked Questions',
        description: 'Get answers to common questions about our services',
        faqs: [
          {
            question: 'What services do you offer?',
            answer: 'We provide comprehensive solutions tailored to your business needs. Contact us to learn more about how we can help you achieve your goals.'
          },
          {
            question: 'How can I get started?',
            answer: 'Simply contact us to discuss your needs. We\'ll schedule a consultation to understand your requirements and provide a customized solution.'
          },
          {
            question: 'What are your pricing options?',
            answer: 'We offer flexible pricing options to suit different budgets. Get in touch for a personalized quote based on your specific requirements.'
          }
        ],
        colors: defaultColors
      } satisfies FaqCard,
    ]
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    description: 'Showcase your creative work and projects',
    icon: '🎨',
    cards: [
      {
        type: 'hero3',
        brandTitle: 'Portfolio',
        title: 'Creative Professional',
        description: 'Showcasing my best work and creative projects. Explore my portfolio to see what I can create for you.',
        button: { title: 'View Projects', url: 'https://stack.je' },
        logo: { src: 'https://placehold.co/128x128', alt: 'Portfolio logo' },
        image: { src: 'https://placehold.co/250x250', alt: 'Creative professional photo' },
        backgroundImage: { src: 'https://placehold.co/800x400', alt: 'Creative background' },
        socialLinks: [
          { platform: 'instagram', url: 'https://instagram.com', displayName: '@yourhandle' },
          { platform: 'dribbble', url: 'https://dribbble.com', displayName: 'Dribbble' },
          { platform: 'email', url: 'mailto:hello@example.com', displayName: 'Hire me' }
        ],
        colors: defaultColors
      } satisfies HeroCard,
      {
        type: 'image3',
        title: 'Recent Work',
        description: 'A selection of my latest projects and creative endeavors',
        images: [
          {
            image: { src: 'https://placehold.co/400x300', alt: 'Project 1' },
            caption: 'Brand Identity Design'
          },
          {
            image: { src: 'https://placehold.co/400x300', alt: 'Project 2' },
            caption: 'Web Design Project'
          },
          {
            image: { src: 'https://placehold.co/400x300', alt: 'Project 3' },
            caption: 'Digital Art Collection'
          },
          {
            image: { src: 'https://placehold.co/400x300', alt: 'Project 4' },
            caption: 'Photography Series'
          }
        ],
        colors: defaultColors
      } satisfies ImageCard,
      {
        type: 'text1',
        title: 'Get In Touch',
        subtitle: 'Ready to work together? I\'d love to hear about your project and discuss how we can bring your vision to life.',
        colors: defaultColors
      } satisfies TextCard,
    ]
  },
  {
    id: 'product-page',
    name: 'Product Page',
    description: 'Launch and showcase your product',
    icon: '📦',
    cards: [
      {
        type: 'hero2',
        brandTitle: 'Product Name',
        title: 'The Solution You Need',
        description: 'Transform your workflow with our innovative product. Designed to solve real problems and deliver real results.',
        button: { title: 'Try It Now', url: 'https://example.com' },
        logo: { src: 'https://placehold.co/128x128', alt: 'Product logo' },
        image: { src: 'https://placehold.co/400x300', alt: 'Product showcase' },
        backgroundImage: { src: 'https://placehold.co/800x400', alt: 'Product background' },
        socialLinks: [
          { platform: 'x', url: 'https://x.com/product', displayName: '@product' },
          { platform: 'email', url: 'mailto:support@example.com', displayName: 'Support' }
        ],
        colors: defaultColors
      } satisfies HeroCard,
      {
        type: 'feature2',
        title: 'Product Features',
        subtitle: 'Everything you need to succeed',
        features: [
          {
            title: 'Easy to Use',
            description: 'Intuitive interface that anyone can master in minutes',
            image: 'https://placehold.co/150'
          },
          {
            title: 'Powerful',
            description: 'Advanced capabilities that scale with your needs',
            image: 'https://placehold.co/150'
          },
          {
            title: 'Reliable',
            description: '99.9% uptime guarantee with 24/7 monitoring',
            image: 'https://placehold.co/150'
          }
        ],
        colors: defaultColors
      } satisfies FeatureCard,
      {
        type: 'faq1',
        title: 'Questions?',
        description: 'Find answers to common questions about our product',
        faqs: [
          {
            question: 'How much does it cost?',
            answer: 'We offer flexible pricing plans to suit different needs. Start with our free trial and upgrade when you\'re ready.'
          },
          {
            question: 'Is there a free trial?',
            answer: 'Yes! Get started with our 14-day free trial. No credit card required.'
          },
          {
            question: 'Do you offer support?',
            answer: 'Absolutely! Our support team is available 24/7 to help you get the most out of our product.'
          }
        ],
        colors: defaultColors
      } satisfies FaqCard,
    ]
  },
  {
    id: 'empty-canvas',
    name: 'Empty Canvas',
    description: 'Start from scratch and build exactly what you want',
    icon: '✨',
    cards: []
  }
];

export function getTemplateById(id: SiteTemplateId): SiteTemplate | undefined {
  return siteTemplates.find(template => template.id === id);
}

export function createConfigFromTemplate(template: SiteTemplate, title?: string, description?: string): Config {
  return {
    title: title || template.name,
    description: description || template.description,
    fontSetId: 'modern',
    pages: [
      {
        path: '/',
        title: title || template.name,
        cards: template.cards
      }
    ]
  };
}